import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { useResizeListener } from "../../hooks/useResizeListener"

export function useOpen({
  closeTime,
  openTime,
  scrollWhenOpen,
  isControlled,
  onToogle,
  initialOpen,
  open: controlledOpen,
}) {
  const [stateOpen, setOpen] = useState(Boolean(initialOpen))
  const titleRef = useRef()
  // needed to know if was toogled
  const openRef = useRef(null)
  const containerRef = useRef()

  const isOpen = useMemo(() => {
    return isControlled ? controlledOpen : stateOpen
  }, [isControlled, controlledOpen, stateOpen])

  const setHeight = useCallback((willOpen) => {
    if (!containerRef.current) return

    if (willOpen) {
      containerRef.current.style.maxHeight = `${containerRef.current.scrollHeight}px`
    } else {
      containerRef.current.style.maxHeight = "0px"
    }
  }, [])

  const onResize = () => setHeight(isOpen)

  useResizeListener({ onResize })

  useEffect(() => {
    setHeight(isOpen)
  }, [isOpen, setHeight])

  useEffect(() => {
    const isMounting = openRef.current === null
    // Do not fire scroll behavior on first mount
    if (isMounting) {
      openRef.current = isOpen

      return
    }

    const wasToogled = openRef.current !== isOpen
    openRef.current = isOpen

    if (!scrollWhenOpen || !isOpen || !wasToogled) return

    // * 1000 since it is on secs
    const scrollInMs = Math.max(closeTime, openTime) * 1000
    const timeoutId = setTimeout(() => {
      if (titleRef.current) {
        titleRef.current.scrollIntoView(true)
      }
    }, scrollInMs)

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId)
      }
    }
  }, [isOpen, scrollWhenOpen, closeTime, openTime])

  const handleToogle = () => {
    if (isControlled) {
      if (onToogle) {
        onToogle(!controlledOpen)
      }

      return
    }

    if (onToogle) {
      onToogle(!stateOpen)
      setOpen(!stateOpen)
    } else {
      setOpen(!stateOpen)
    }
  }

  return { titleRef, isOpen, setOpen, handleToogle, containerRef }
}
